import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, Calendar, MapPin, Award, TrendingUp, TrendingDown, Edit, PlusCircle, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { usePlayer } from '../../context/PlayerContext';
import { convertBaseballInningsToDecimal, formatBattingStat, calculateBattingAverage } from '../../utils/calculations';

const GameSummaryModal = ({ game, onClose, onEdit, onSuccess }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAdvancedBattingStats, setShowAdvancedBattingStats] = useState(false);
  const [showAdvancedPitchingStats, setShowAdvancedPitchingStats] = useState(false);
  const { updateGame, playerData, isLoading } = usePlayer();
  
  const [gameData, setGameData] = useState({
    date: '',
    opponent: '',
    league: '',
    // Bat information
    batMaker: '',
    batWoodType: '',
    batKnobType: 'Standard',
    batWeight: 30,
    batLength: 33,
    batModel: '',
    // Basic batting stats
    atBats: 0,
    hits: 0,
    doubles: 0,
    triples: 0,
    homeRuns: 0,
    runs: 0,
    rbi: 0,
    walks: 0,
    battingStrikeouts: 0,
    hitByPitch: 0,
    // Advanced batting stats
    sacrificeFlies: 0,
    sacrificeBunts: 0,
    stolenBases: 0,
    caughtStealing: 0,
    groundOuts: 0,
    flyOuts: 0,
    plateAppearances: 0,
    leftOnBase: 0,
    // Pitching stats
    didPitch: false,
    inningsPitched: '',
    pitchingStrikeouts: 0,
    hitsAllowed: 0,
    walksAllowed: 0,
    earnedRuns: 0,
    pitchingResult: 'No Decision',
    // Advanced pitching stats
    pitchCount: 0,
    strikes: 0,
    balls: 0,
    wildPitches: 0,
    hitBatters: 0,
    balks: 0,
    pickoffs: 0,
    groundBallOuts: 0,
    flyBallOuts: 0,
    completedGame: false,
    shutout: false,
    // Notes
    notes: ''
  });
  
  // Initialize form with game data
  useEffect(() => {
    if (game) {
      const battingStats = game.battingStats || {};
      const pitchingStats = game.pitchingStats || {};
      const batInfo = game.batInfo || {};
      
      setGameData({
        date: game.date ? new Date(game.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        opponent: game.opponent || '',
        league: game.league || (playerData?.leagues?.length > 0 ? playerData.leagues[0] : ''),
        // Bat information
        batMaker: batInfo.maker || '',
        batWoodType: batInfo.woodType || '',
        batKnobType: batInfo.knobType || 'Standard',
        batWeight: batInfo.weight || 30,
        batLength: batInfo.length || 33,
        batModel: batInfo.model || '',
        // Basic batting stats
        atBats: battingStats.atBats || 0,
        hits: battingStats.hits || 0,
        doubles: battingStats.doubles || 0,
        triples: battingStats.triples || 0,
        homeRuns: battingStats.homeRuns || 0,
        runs: battingStats.runs || 0,
        rbi: battingStats.rbi || 0,
        walks: battingStats.walks || 0,
        battingStrikeouts: battingStats.strikeouts || 0,
        hitByPitch: battingStats.hitByPitch || 0,
        // Advanced batting stats
        sacrificeFlies: battingStats.sacrificeFlies || 0,
        sacrificeBunts: battingStats.sacrificeBunts || 0,
        stolenBases: battingStats.stolenBases || 0,
        caughtStealing: battingStats.caughtStealing || 0,
        groundOuts: battingStats.groundOuts || 0,
        flyOuts: battingStats.flyOuts || 0,
        plateAppearances: battingStats.plateAppearances || 0,
        leftOnBase: battingStats.leftOnBase || 0,
        // Pitching stats
        didPitch: !!pitchingStats.inningsPitched,
        inningsPitched: pitchingStats.inningsPitched || '',
        pitchingStrikeouts: pitchingStats.strikeouts || 0,
        hitsAllowed: pitchingStats.hits || 0,
        walksAllowed: pitchingStats.walks || 0,
        earnedRuns: pitchingStats.earnedRuns || 0,
        pitchingResult: pitchingStats.result || 'No Decision',
        // Advanced pitching stats
        pitchCount: pitchingStats.pitchCount || 0,
        strikes: pitchingStats.strikes || 0,
        balls: pitchingStats.balls || 0,
        wildPitches: pitchingStats.wildPitches || 0,
        hitBatters: pitchingStats.hitBatters || 0,
        balks: pitchingStats.balks || 0,
        pickoffs: pitchingStats.pickoffs || 0,
        groundBallOuts: pitchingStats.groundBallOuts || 0,
        flyBallOuts: pitchingStats.flyBallOuts || 0,
        completedGame: pitchingStats.completedGame || false,
        shutout: pitchingStats.shutout || false,
        // Notes
        notes: game.notes || ''
      });
    }
  }, [game, playerData]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setGameData({
      ...gameData,
      [name]: newValue
    });
  };
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    const formattedGame = {
      date: new Date(gameData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      opponent: gameData.opponent,
      league: gameData.league,
      notes: gameData.notes,
      // Add bat information
      batInfo: {
        maker: gameData.batMaker,
        woodType: gameData.batWoodType,
        knobType: gameData.batKnobType,
        weight: parseFloat(gameData.batWeight),
        length: parseFloat(gameData.batLength),
        model: gameData.batModel
      },
      battingStats: {
        // Basic batting stats
        atBats: Number(gameData.atBats),
        hits: Number(gameData.hits),
        doubles: Number(gameData.doubles),
        triples: Number(gameData.triples),
        homeRuns: Number(gameData.homeRuns),
        runs: Number(gameData.runs),
        rbi: Number(gameData.rbi),
        walks: Number(gameData.walks),
        strikeouts: Number(gameData.battingStrikeouts),
        hitByPitch: Number(gameData.hitByPitch),
        // Advanced batting stats (only include if they have values)
        ...(gameData.sacrificeFlies > 0 && { sacrificeFlies: Number(gameData.sacrificeFlies) }),
        ...(gameData.sacrificeBunts > 0 && { sacrificeBunts: Number(gameData.sacrificeBunts) }),
        ...(gameData.stolenBases > 0 && { stolenBases: Number(gameData.stolenBases) }),
        ...(gameData.caughtStealing > 0 && { caughtStealing: Number(gameData.caughtStealing) }),
        ...(gameData.groundOuts > 0 && { groundOuts: Number(gameData.groundOuts) }),
        ...(gameData.flyOuts > 0 && { flyOuts: Number(gameData.flyOuts) }),
        ...(gameData.plateAppearances > 0 && { plateAppearances: Number(gameData.plateAppearances) }),
        ...(gameData.leftOnBase > 0 && { leftOnBase: Number(gameData.leftOnBase) })
      }
    };
    
    // Add pitching stats if applicable
    if (gameData.didPitch) {
      formattedGame.pitchingStats = {
        // Basic pitching stats
        inningsPitched: convertBaseballInningsToDecimal(gameData.inningsPitched),
        strikeouts: Number(gameData.pitchingStrikeouts),
        hits: Number(gameData.hitsAllowed),
        walks: Number(gameData.walksAllowed),
        earnedRuns: Number(gameData.earnedRuns),
        result: gameData.pitchingResult,
        // Advanced pitching stats (only include if they have values)
        ...(gameData.pitchCount > 0 && { pitchCount: Number(gameData.pitchCount) }),
        ...(gameData.strikes > 0 && { strikes: Number(gameData.strikes) }),
        ...(gameData.balls > 0 && { balls: Number(gameData.balls) }),
        ...(gameData.wildPitches > 0 && { wildPitches: Number(gameData.wildPitches) }),
        ...(gameData.hitBatters > 0 && { hitBatters: Number(gameData.hitBatters) }),
        ...(gameData.balks > 0 && { balks: Number(gameData.balks) }),
        ...(gameData.pickoffs > 0 && { pickoffs: Number(gameData.pickoffs) }),
        ...(gameData.groundBallOuts > 0 && { groundBallOuts: Number(gameData.groundBallOuts) }),
        ...(gameData.flyBallOuts > 0 && { flyBallOuts: Number(gameData.flyBallOuts) }),
        ...(gameData.completedGame && { completedGame: gameData.completedGame }),
        ...(gameData.shutout && { shutout: gameData.shutout })
      };
    }
    
    // Preserve the seasonId if it exists
    if (game.seasonId) {
      formattedGame.seasonId = game.seasonId;
    }
    
    // Update the game
    const success = await updateGame(game.id, formattedGame);
    
    if (success) {
      setIsEditMode(false);
      if (onSuccess) onSuccess();
    }
  };
  
  if (!game) return null;

  // Format date
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown date';
      
      // Handle different date formats
      const date = new Date(dateString);
      return isNaN(date.getTime()) 
        ? dateString 
        : format(date, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      return dateString || 'Unknown date';
    }
  };

  // Calculate batting average for this game
  const calculateBattingAvg = () => {
    if (!game.battingStats || !game.battingStats.atBats || game.battingStats.atBats === 0) {
      return '.000';
    }

    const avg = calculateBattingAverage(game.battingStats.hits, game.battingStats.atBats);
    return formatBattingStat(avg);
  };

  // Calculate ERA for this game
  const calculateERA = () => {
    if (!game.pitchingStats || !game.pitchingStats.inningsPitched || game.pitchingStats.inningsPitched === 0) {
      return '0.00';
    }
    
    const era = (game.pitchingStats.earnedRuns * 9) / game.pitchingStats.inningsPitched;
    return era.toFixed(2);
  };

  // Format batting line (H-AB format)
  const formatBattingLine = () => {
    if (!game.battingStats) return 'No batting stats';
    
    const hits = Number(game.battingStats.hits) || 0;
    const atBats = Number(game.battingStats.atBats) || 0;
    
    return `${hits}-${atBats}`;
  };

  // Check if this was a multi-hit game
  const isMultiHitGame = () => {
    return game.battingStats && game.battingStats.hits >= 2;
  };

  // Check if this was a home run game
  const isHomeRunGame = () => {
    return game.battingStats && game.battingStats.homeRuns >= 1;
  };

  // Check if this was a quality start (for pitchers)
  const isQualityStart = () => {
    return game.pitchingStats && 
           game.pitchingStats.inningsPitched >= 6 && 
           game.pitchingStats.earnedRuns <= 3;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-dark-200 rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gray-50 dark:bg-dark-300 p-4 rounded-t-lg border-b border-gray-200 dark:border-dark-400 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold dark:text-darkText-primary">Game Summary</h2>
            <p className="text-gray-600 dark:text-darkText-secondary text-sm">
              vs {game.opponent} • {formatDate(game.date)}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Game Details or Edit Form */}
        {!isEditMode ? (
          <div className="p-6">
            {/* Game Info */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center">
                <Calendar size={18} className="text-gray-500 dark:text-darkText-tertiary mr-2" />
                <span className="dark:text-darkText-primary">{formatDate(game.date)}</span>
              </div>
              {game.location && (
                <div className="flex items-center">
                  <MapPin size={18} className="text-gray-500 dark:text-darkText-tertiary mr-2" />
                  <span className="dark:text-darkText-primary">{game.location}</span>
                </div>
              )}
              {game.league && (
                <div className="flex items-center">
                  <Award size={18} className="text-gray-500 dark:text-darkText-tertiary mr-2" />
                  <span className="dark:text-darkText-primary">{game.league}</span>
                </div>
              )}
            </div>
            
            {/* Bat Information */}
            {game.batInfo && (
              <div className="mb-6 bg-gray-50 dark:bg-dark-300 p-4 rounded-md">
                <h3 className="text-lg font-semibold dark:text-darkText-primary mb-3">Bat Used</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
                  {game.batInfo.maker && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-darkText-tertiary">Maker:</span>
                      <span className="text-sm font-medium dark:text-darkText-secondary">{game.batInfo.maker}</span>
                    </div>
                  )}
                  {game.batInfo.woodType && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-darkText-tertiary">Wood Type:</span>
                      <span className="text-sm font-medium dark:text-darkText-secondary">{game.batInfo.woodType}</span>
                    </div>
                  )}
                  {game.batInfo.knobType && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-darkText-tertiary">Knob Type:</span>
                      <span className="text-sm font-medium dark:text-darkText-secondary">{game.batInfo.knobType}</span>
                    </div>
                  )}
                  {game.batInfo.length && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-darkText-tertiary">Length:</span>
                      <span className="text-sm font-medium dark:text-darkText-secondary">{game.batInfo.length}" inches</span>
                    </div>
                  )}
                  {game.batInfo.weight && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-darkText-tertiary">Weight:</span>
                      <span className="text-sm font-medium dark:text-darkText-secondary">{game.batInfo.weight} oz</span>
                    </div>
                  )}
                  {game.batInfo.model && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-darkText-tertiary">Model:</span>
                      <span className="text-sm font-medium dark:text-darkText-secondary">{game.batInfo.model}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-dark-400">
              <button
                onClick={() => setIsEditMode(true)}
                className="flex items-center px-4 py-2 bg-white dark:bg-dark-300 border border-gray-300 dark:border-dark-400 rounded-md text-gray-700 dark:text-darkText-primary hover:bg-gray-50 dark:hover:bg-dark-400 transition-colors"
              >
                <Edit size={16} className="mr-2" />
                Edit Game
              </button>
              {!game.notes && (
                <button
                  onClick={() => setIsEditMode(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                >
                  <PlusCircle size={16} className="mr-2" />
                  Add Notes
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Game Info Section */}
              <div className="grid grid-cols-2 gap-4">
                {/* Date Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                    Date
                  </label>
                  <input 
                    type="date" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                    name="date"
                    value={gameData.date}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                {/* Opponent Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                    Opponent
                  </label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                    placeholder="Team name"
                    name="opponent"
                    value={gameData.opponent}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              {/* League Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                  League
                </label>
                <select 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                  name="league"
                  value={gameData.league}
                  onChange={handleChange}
                >
                  {/* Map through player's leagues or show a default if none exist */}
                  {playerData?.leagues?.length > 0 ? (
                    playerData.leagues.map((league, index) => (
                      <option key={index} value={league}>{league}</option>
                    ))
                  ) : (
                    <option value="">No leagues available</option>
                  )}
                </select>
              </div>
              
              {/* Bat Information Section */}
              <div className="border-b border-gray-200 dark:border-dark-400 pb-4">
                <h3 className="text-lg font-medium dark:text-darkText-primary mb-3">Bat Information</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-3">
                  {/* Bat Maker */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                      Bat Maker
                    </label>
                    <select 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                      name="batMaker"
                      value={gameData.batMaker}
                      onChange={handleChange}
                    >
                      <option value="">Select Bat Maker</option>
                      <option value="Marucci">Marucci</option>
                      <option value="Victus">Victus</option>
                      <option value="Louisville Slugger">Louisville Slugger</option>
                      <option value="Mizuno">Mizuno</option>
                      <option value="Rawlings">Rawlings</option>
                      <option value="DeMarini">DeMarini</option>
                      <option value="Old Hickory">Old Hickory</option>
                      <option value="Beaver Bats">Beaver Bats</option>
                      <option value="Warstic">Warstic</option>
                      <option value="Nike">Nike</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  {/* Wood Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                      Wood Type
                    </label>
                    <select 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                      name="batWoodType"
                      value={gameData.batWoodType}
                      onChange={handleChange}
                    >
                      <option value="">Select Wood Type</option>
                      <option value="Maple">Maple</option>
                      <option value="Hickory">Hickory</option>
                      <option value="Bamboo">Bamboo</option>
                      <option value="Ash">Ash</option>
                      <option value="Birch">Birch</option>
                      <option value="Composite">Composite</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-3">
                  {/* Knob Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                      Knob Type
                    </label>
                    <select 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                      name="batKnobType"
                      value={gameData.batKnobType}
                      onChange={handleChange}
                    >
                      <option value="Standard">Standard</option>
                      <option value="Flared">Flared</option>
                      <option value="Axe">Axe</option>
                      <option value="Thin">Thin</option>
                    </select>
                  </div>
                  
                  {/* Bat Model */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                      Bat Model (Optional)
                    </label>
                    <input 
                      type="text" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                      placeholder="e.g. AP5, 271, etc."
                      name="batModel"
                      value={gameData.batModel}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Bat Length */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                      Bat Length: {gameData.batLength} inches
                    </label>
                    <input 
                      type="range" 
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-dark-400"
                      min="20"
                      max="40"
                      step="0.5"
                      name="batLength"
                      value={gameData.batLength}
                      onChange={handleChange}
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-darkText-tertiary mt-1">
                      <span>20"</span>
                      <span>30"</span>
                      <span>40"</span>
                    </div>
                  </div>
                  
                  {/* Bat Weight */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                      Bat Weight: {gameData.batWeight} oz
                    </label>
                    <input 
                      type="range" 
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-dark-400"
                      min="20"
                      max="45"
                      step="0.5"
                      name="batWeight"
                      value={gameData.batWeight}
                      onChange={handleChange}
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-darkText-tertiary mt-1">
                      <span>20oz</span>
                      <span>32.5oz</span>
                      <span>45oz</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Batting Stats Section */}
              <div className="border-b border-gray-200 dark:border-dark-400 pb-2">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium dark:text-darkText-primary">Batting Stats</h3>
                  <button 
                    type="button"
                    onClick={() => setShowAdvancedBattingStats(!showAdvancedBattingStats)}
                    className="text-blue-600 dark:text-blue-400 text-sm flex items-center"
                  >
                    {showAdvancedBattingStats ? 'Hide Advanced Stats' : 'Show Advanced Stats'}
                    {showAdvancedBattingStats ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
                  </button>
                </div>
                
                {/* Basic batting stats inputs */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">At Bats</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="atBats"
                      value={gameData.atBats}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Hits</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="hits"
                      value={gameData.hits}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">2B</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="doubles"
                      value={gameData.doubles}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">3B</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="triples"
                      value={gameData.triples}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">HR</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="homeRuns"
                      value={gameData.homeRuns}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Runs</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="runs"
                      value={gameData.runs}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">RBI</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="rbi"
                      value={gameData.rbi}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">BB</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="walks"
                      value={gameData.walks}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">K</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="battingStrikeouts"
                      value={gameData.battingStrikeouts}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">HBP</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="hitByPitch"
                      value={gameData.hitByPitch}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                {/* Advanced batting stats */}
                {showAdvancedBattingStats && (
                  <div className="mt-4 pt-2 border-t border-gray-200 dark:border-dark-400">
                    <h4 className="text-md font-medium dark:text-darkText-primary mb-2">Advanced Batting Stats</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Sacrifice Flies</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="sacrificeFlies"
                          value={gameData.sacrificeFlies}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Sacrifice Bunts</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="sacrificeBunts"
                          value={gameData.sacrificeBunts}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Stolen Bases</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="stolenBases"
                          value={gameData.stolenBases}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Caught Stealing</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="caughtStealing"
                          value={gameData.caughtStealing}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Ground Outs</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="groundOuts"
                          value={gameData.groundOuts}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Fly Outs</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="flyOuts"
                          value={gameData.flyOuts}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Plate Appearances</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="plateAppearances"
                          value={gameData.plateAppearances}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Left On Base</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="leftOnBase"
                          value={gameData.leftOnBase}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Pitching Stats Section */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center">
                    <h3 className="text-lg font-medium dark:text-darkText-primary">Pitching Stats</h3>
                    <div className="ml-2">
                      <input 
                        type="checkbox" 
                        id="didPitch" 
                        className="mr-1 dark:bg-dark-300 dark:border-dark-400"
                        name="didPitch"
                        checked={gameData.didPitch}
                        onChange={handleChange}
                      />
                      <label htmlFor="didPitch" className="text-sm dark:text-darkText-primary">I pitched in this game</label>
                    </div>
                  </div>
                  {gameData.didPitch && (
                    <button 
                      type="button"
                      onClick={() => setShowAdvancedPitchingStats(!showAdvancedPitchingStats)}
                      className="text-blue-600 dark:text-blue-400 text-sm flex items-center"
                    >
                      {showAdvancedPitchingStats ? 'Hide Advanced Stats' : 'Show Advanced Stats'}
                      {showAdvancedPitchingStats ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
                    </button>
                  )}
                </div>
                
                {/* Pitching stats inputs */}
                {gameData.didPitch && (
                  <div className="space-y-4 mt-2">
                    {/* Basic Pitching Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Innings Pitched</label>
                        <input 
                          type="text" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                          placeholder="e.g. 6.2 for 6⅔ innings"
                          name="inningsPitched"
                          value={gameData.inningsPitched}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Strikeouts</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="pitchingStrikeouts"
                          value={gameData.pitchingStrikeouts}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Hits Allowed</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="hitsAllowed"
                          value={gameData.hitsAllowed}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Walks Allowed</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="walksAllowed"
                          value={gameData.walksAllowed}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Earned Runs</label>
                        <input 
                          type="number" 
                          className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                          min="0"
                          name="earnedRuns"
                          value={gameData.earnedRuns}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Result</label>
                      <select 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                        name="pitchingResult"
                        value={gameData.pitchingResult}
                        onChange={handleChange}
                      >
                        <option value="No Decision">No Decision</option>
                        <option value="Win">Win</option>
                        <option value="Loss">Loss</option>
                        <option value="Save">Save</option>
                        <option value="Hold">Hold</option>
                        <option value="Blown Save">Blown Save</option>
                      </select>
                    </div>
                    
                    {/* Advanced Pitching Stats */}
                    {showAdvancedPitchingStats && (
                      <div className="mt-4 pt-2 border-t border-gray-200 dark:border-dark-400">
                        <h4 className="text-md font-medium dark:text-darkText-primary mb-2">Advanced Pitching Stats</h4>
                        
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Pitch Count</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="pitchCount"
                              value={gameData.pitchCount}
                              onChange={handleChange}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Strikes</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="strikes"
                              value={gameData.strikes}
                              onChange={handleChange}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Balls</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="balls"
                              value={gameData.balls}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Wild Pitches</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="wildPitches"
                              value={gameData.wildPitches}
                              onChange={handleChange}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Hit Batters</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="hitBatters"
                              value={gameData.hitBatters}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Balks</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="balks"
                              value={gameData.balks}
                              onChange={handleChange}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Pickoffs</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="pickoffs"
                              value={gameData.pickoffs}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Ground Ball Outs</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="groundBallOuts"
                              value={gameData.groundBallOuts}
                              onChange={handleChange}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Fly Ball Outs</label>
                            <input 
                              type="number" 
                              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                              min="0"
                              name="flyBallOuts"
                              value={gameData.flyBallOuts}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div className="flex items-center">
                            <input 
                              type="checkbox" 
                              id="completedGame" 
                              className="mr-2 dark:bg-dark-300 dark:border-dark-400"
                              name="completedGame"
                              checked={gameData.completedGame}
                              onChange={handleChange}
                            />
                            <label htmlFor="completedGame" className="text-sm dark:text-darkText-primary">Completed Game</label>
                          </div>
                          <div className="flex items-center">
                            <input 
                              type="checkbox" 
                              id="shutout" 
                              className="mr-2 dark:bg-dark-300 dark:border-dark-400"
                              name="shutout"
                              checked={gameData.shutout}
                              onChange={handleChange}
                            />
                            <label htmlFor="shutout" className="text-sm dark:text-darkText-primary">Shutout</label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Notes Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                  Game Notes
                </label>
                <textarea
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                  rows="4"
                  placeholder="Add any notes about this game..."
                  name="notes"
                  value={gameData.notes}
                  onChange={handleChange}
                ></textarea>
              </div>
              
              {/* Submit Button */}
              <div className="pt-4 flex justify-between">
                <button
                  type="button"
                  onClick={() => setIsEditMode(false)}
                  className="bg-gray-300 dark:bg-dark-400 text-gray-700 dark:text-darkText-primary py-2 px-4 rounded-md hover:bg-gray-400 dark:hover:bg-dark-300 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={isLoading}
                  className="bg-blue-600 dark:bg-blue-700 text-white py-2 px-4 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-t-2 border-b-2 border-white rounded-full mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

GameSummaryModal.propTypes = {
  game: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onSuccess: PropTypes.func
};

export default GameSummaryModal;
